<template>
  <div class="sales__report">
    <ReportFormat @show_result="show_result" :is_table_show="false" :isLoading="loading">
      <template #report-form>
        <div class="vx-row pt-5 px-5">
          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>From Date</h6>
              </div>
              <div class="vx-col w-3/5 relative">
                <date-picker :appendToBody="false" v-model="fromDate" placeholder="MM-DD-YYYY" format="MM-DD-YYYY" valueType="format" class="w-full" name="Report From Date" />
              </div>
            </div>

            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>User Name</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  class="w-full"
                  autocomplete
                  :value="user"
                  @input="(elem) => (user = elem.value)"
                  placeholder="Please Select Username"
                  :extra="[{ label: 'All', value: null }]"
                  :options="sales_user_list_options"
                />
              </div>
            </div>
          </div>

          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>To Date</h6>
              </div>
              <div class="vx-col w-3/5 relative">
                <date-picker :appendToBody="false" v-model="toDate" placeholder="MM-DD-YYYY" format="MM-DD-YYYY" valueType="format" class="w-full" v-validate="'required'" name="Report To Date" />
              </div>
            </div>

            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Register</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  class="w-full"
                  autocomplete
                  :value="registerId"
                  @input="(elem) => (registerId = elem.value)"
                  placeholder="Please Select Register"
                  label="register_name"
                  val="id"
                  action="register/getRegisterList"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </ReportFormat>

    <!-- table -->
    <SalesReportTable ref="saleReport" :fromDate="fromDate" :toDate="toDate" :user="userDetail" :register="registerDetail" @exportReport="exportReport" />

    <!-- pdf container -->
    <div id="pdf-container"></div>
  </div>
</template>

<script>
import XLSX from 'xlsx'
import moment from 'moment'
import Vue from 'vue'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import { mapState, mapMutations } from 'vuex'
import ReportFormat from '@/views/reports/components/ReportFormat.vue'
import SalesReportTable from '@/views/reports/components/SalesReportTable.vue'
import SalesReportPdf from '@/views/reports/components/pdf/SalesReportPdf.vue'
import Select2 from '@/components/custom/form-elements/Select2.vue'

export default {
  name: 'SalesReport',

  components: {
    ReportFormat,
    DatePicker,
    SalesReportTable,
    SalesReportPdf,
    Select2
  },

  data() {
    return {
      date: 'MM-dd-yyyy',
      fromDate: moment().format('MM-DD-YYYY'),
      user: null,
      userDetail: null,
      toDate: moment().format('MM-DD-YYYY'),
      generating: false,
      registerId: null,
      registerDetail: null,
      loading: false
    }
  },

  async mounted() {
    // fetch sales user list
    await this.getSalesUserList()
  },

  methods: {
    moment,

    ...mapMutations('report', { set_state: 'SET_STATE' }),
    ...mapMutations('register', { set_registers_state: 'SET_STATE' }),

    // get sales user list
    async getSalesUserList() {
      this.loading = true
      this.$store
        .dispatch('report/getSalesUserList')
        .then(() => {
          this.loading = false
        })
        .catch((err) => {
          const { message } = err
          this.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    // fetch report
    async show_result() {
      this.loading = true
      const payload = {
        fromDate: moment(this.fromDate).format('YYYY-MM-DD'),
        toDate: moment(this.toDate).format('YYYY-MM-DD'),
        userId: this.user || null,
        registerId: this.registerId
      }

      this.$store
        .dispatch('report/getSalesReport', payload)
        .then(() => {
          this.loading = false
        })
        .catch((err) => {
          const { message } = err
          this.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    // export report
    async exportReport(type) {
      if (type === 'excel') {
        await this.generateExcel()
      }

      if (type === 'pdf') {
        await this.generatePdf()
      }
    },

    // generate excel
    async generateExcel() {
      this.generating = true

      const wb = XLSX.utils.book_new()

      const ws = XLSX.utils.table_to_sheet(this.$refs.saleReport.$refs.reportTable.$el)
      const reportTable = XLSX.utils.sheet_to_json(ws)
      XLSX.utils.sheet_add_json(ws, reportTable, { origin: 'A4' })

      XLSX.utils.sheet_add_aoa(ws, [['Overall Sales']], { origin: 'A1' })
      XLSX.utils.sheet_add_aoa(ws, [[moment(this.fromDate).format('MM/DD/YYYY hh:mm:ss A'), null, moment(this.toDate).format('MM/DD/YYYY hh:mm:ss A')]], { origin: 'A2' })
      XLSX.utils.sheet_add_aoa(ws, [['Employess Name', null, this.user && this.user.username]], { origin: 'A3' })

      const mergedCells = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } },
        { s: { r: 1, c: 2 }, e: { r: 1, c: 3 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 1 } },
        { s: { r: 2, c: 2 }, e: { r: 2, c: 3 } }
      ]

      ws['!merges'] = mergedCells

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1')
      XLSX.writeFile(wb, 'Sales Report.xlsx')

      this.generating = false
    },

    // generate pdf
    async generatePdf() {
      this.generating = true

      const ap = document.querySelector('#mount-node')
      if (ap) {
        ap.remove()
      }

      const elem = document.querySelector('#pdf-container')
      const mountNode = document.createElement('div')
      mountNode.id = 'mount-node'
      elem.appendChild(mountNode)

      const pdfReport = Vue.extend(SalesReportPdf)
      new pdfReport({
        store: this.$store,
        propsData: {
          fromDate: this.fromDate,
          toDate: this.toDate,
          user: this.user
        }
      })
        .$on('downloaded', () => {
          this.generating = false
        })
        .$mount('#mount-node')
    }
  },

  computed: {
    ...mapState('report', ['reports_data', 'sales_user_list']),

    sales_user_list_options() {
      return this.sales_user_list.reduce((acc, item) => {
        item.label = item.username
        item.value = item.id

        acc.push(item)

        return acc
      }, [])
    }
  },

  watch: {
    user() {
      this.userDetail = this.sales_user_list_options.find((x) => x.id === this.user)
    },

    registerId() {
      this.registerDetail = this.registerOptions.find((x) => x.id === this.registerId)
    }
  },

  async beforeDestroy() {
    await this.set_state({ action: 'reports_data', data: [] })
    await this.set_registers_state({ action: 'registers', data: [] })
  }
}
</script>

<style></style>
